import classnames from 'classnames';
import { isNull, isEqual } from 'lodash/fp';
import React, { useCallback, useMemo } from 'react';

import { useAppDispatch, useAppSelector } from '../../../hooks/react-redux';
import { getUserHasFeaturePermissionNoAdmin } from '../../auth/login/store';
import { LoadingDots } from '../../shared/loading-dots/LoadingDots';
import { Scrollable } from '../../shared/scrollable/Scrollable';
import { getAvailableSubCounterpartyTypes, getIsEditingSelectedSubCounterparty, getIsFetchingSubCounterpartyTypes, getSelectedSubCounterpartyType, getSubCounterpartyScopeFilters, setSelectedOpinionSubCounterparty, SubCounterpartyType } from '../sub-counterparty-types/store';
import { FeaturePermission } from '../users/store';
import { SubCounterpartyInput } from './SubCounterpartyInput';
import styles from './SubCounterpartyTypes.module.scss';
import { OpinionScope } from '../opinions/store';
import { scopeFormatter } from '../../shared/table/arkTableFormatters';

interface SubCounterpartyInputSectionProps {
    parentOptionsHeight: number;
    jurisdiction: string | null;
    parentCounterparty: string | null;
    initialOffset: number;
}

export const SubCounterpartyInputSection: React.FC<SubCounterpartyInputSectionProps> = ({ parentOptionsHeight, jurisdiction, parentCounterparty, initialOffset }) => {
    const dispatch = useAppDispatch();

    const userHasIndustryStandardPermission = useAppSelector(getUserHasFeaturePermissionNoAdmin([FeaturePermission.MANAGE_INDUSTRY_STANDARD_OPINIONS]));

    const allSubCounterpartyTypes = useAppSelector(getAvailableSubCounterpartyTypes);
    const selectedSubCounterparty = useAppSelector(getSelectedSubCounterpartyType);
    const isLoading = useAppSelector(getIsFetchingSubCounterpartyTypes);
    const isEditing = useAppSelector(getIsEditingSelectedSubCounterparty);
    const selectedScopeFilters = useAppSelector(getSubCounterpartyScopeFilters);

    const scopeFilters = useMemo(() => selectedScopeFilters.length === 0 ? Object.values(OpinionScope) : selectedScopeFilters, [selectedScopeFilters]);

    const setSelectedSubCounterparty = useCallback((value: SubCounterpartyType) => {
        if (!isEditing) {
            let selected: SubCounterpartyType | null = value;
            if (isEqual(value, selectedSubCounterparty)) {
                selected = null;
            }
            dispatch(setSelectedOpinionSubCounterparty(selected));
        }
    }, [dispatch, selectedSubCounterparty, isEditing]);

    const subCounterpartyTypes = useMemo(() => allSubCounterpartyTypes.filter(({ parentCounterpartyType, scopes }) => parentCounterparty === parentCounterpartyType && scopes.some(scope => scopeFilters.includes(scope))), [allSubCounterpartyTypes, parentCounterparty, scopeFilters]);

    const noSubCounterpartyTypesMessage = useMemo(() => {
        if (!jurisdiction) {
            return 'Select a jurisdiction and parent counterparty type to add or edit sub counterparty types';
        }
        if (!parentCounterparty) {
            return 'Select a parent counterparty type to edit existing sub counterparty types or click + to add a new sub counterparty type';
        }
        if (subCounterpartyTypes.length === 0) {
            return 'Click + to add a new sub counterparty type';
        }
        return null;
    }, [jurisdiction, parentCounterparty, subCounterpartyTypes]);

    const getIsNotPermitted = useCallback((isSystem: number) => isSystem === 1 && !userHasIndustryStandardPermission, [userHasIndustryStandardPermission]);
    const getScopesDisplay = useCallback((scopes: OpinionScope[]) => `Scope: ${scopes.map(scope => scopeFormatter(scope)).join(', ')}`, []);

    if (isLoading) {
        return (
            <div className={styles.subCounterpartyTypesSectionWrapper}>
                <LoadingDots />
            </div>
        );
    }

    return (
        <div className={styles.subCounterpartyTypesSectionWrapper}>
            {!isNull(jurisdiction) && parentCounterparty && (subCounterpartyTypes.length > 0 || selectedSubCounterparty) ?
                (
                    <div className={styles.subCounterpartyTypesListWrapper}>
                        <Scrollable height={`${parentOptionsHeight + 60}px`}>
                            {subCounterpartyTypes.map((subCounterparty, index) => {
                                const { subCounterpartyName, subCounterpartyTypeId, subCounterpartyDescription, isSystem, scopes } = subCounterparty;
                                const selected = subCounterpartyTypeId === selectedSubCounterparty?.subCounterpartyTypeId;
                                const isNotPermitted = getIsNotPermitted(isSystem);
                                const scopesDisplay = getScopesDisplay(scopes);
                                return (
                                    <div className={classnames(styles.subCounterpartyTile, { [styles.disabledTile]: isEditing || isNotPermitted, [styles.selectedTile]: selected })} onClick={() => setSelectedSubCounterparty(subCounterparty)} key={index}>
                                        <div className={styles.subCounterpartyName}>{subCounterpartyName}</div>
                                        <div className={styles.subCounterpartyDescription}>{subCounterpartyDescription}</div>
                                        <div className={styles.subCounterpartyScopes}>{scopesDisplay}</div>
                                    </div>
                                );
                            })}
                        </Scrollable>
                    </div>
                ) : (
                    <div className={styles.selectMessage}>{noSubCounterpartyTypesMessage}</div>
                )
            }
            <SubCounterpartyInput
                subCounterpartyTypes={allSubCounterpartyTypes}
                selectedSubCounterparty={selectedSubCounterparty}
                userHasIndustryStandardPermission={userHasIndustryStandardPermission}
                initialOffset={{ x: initialOffset, y: 170 }}
                shouldShowDelete
            />
        </div>
    );
};
